//src/components/AddEditBOM.jsx :-
import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, Typography, Grid, IconButton } from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove'; // Import the remove icon
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { grey } from "@mui/material/colors";
import axios from "axios";
import env from "react-dotenv";

const AddEditBOM = ({ onClose, onSave, editData, onExpandDrawer }) => {
    const [showNewProductForm, setShowNewProductForm] = useState(false);
    const [showNewRawMaterialForm, setShowNewRawMaterialForm] = useState(false);
    const [showNewChemicalForm, setShowNewChemicalForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        product_id: "",
        part_id: "",
        part_name: "",
        quantity_required: "",
        chemical_id: "",
        chemical_name: "",
        chemical_quantity: "",
        is_active: true
    });

    const [chemicalEntries, setChemicalEntries] = useState([{ chemical_id: "", chemical_name: "", chemical_quantity: "" }]); // State for chemical entries

    const [options, setOptions] = useState({
        products: [],
        parts: [],
        chemicals: [],
    });

    useEffect(() => {
        fetchDropdownData();
        if (editData) {
            setFormData(editData);
            // If editData has chemicals, set them in chemicalEntries
            if (editData.chemicals) {
                setChemicalEntries(editData.chemicals.map(chemical => ({
                    ...chemical,
                    // Ensure you include the ID for updates
                    id: chemical.id // Assuming this is the ID field in your chemicals data
                })));
            }
        }
    }, [editData]);

    const fetchDropdownData = async () => {
        try {
            const [products, parts, chemicals] = await Promise.all([
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`),
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`),
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`)
            ]);
            setOptions({
                products: products.data,
                parts: parts.data,
                chemicals: chemicals.data,
            });
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    const handleChange = (e, field, idField, optionsArray = [], optionKey = "") => {
        const { value } = e.target;
    
        if (value === "other") {
            // Show the appropriate new form based on the field
            if (field === "product_id") {
                setShowNewProductForm(true);
            } else if (field === "part_id") {
                setShowNewRawMaterialForm(true);
            } else if (field === "chemical_id") {
                setShowNewChemicalForm(true);
            }
            onExpandDrawer();
            // Reset the form data for the selected field
            setFormData(prevState => ({
                ...prevState,
                [field]: "",
                [idField]: ""
            }));
        } else {
            // Hide the new forms
            setShowNewProductForm(false);
            setShowNewRawMaterialForm(false);
            setShowNewChemicalForm(false);
    
            // If optionsArray is provided, find the selected item
            if (optionsArray.length > 0 && optionKey) {
                const selectedItem = optionsArray.find(item => item[optionKey] === value) || {};
                setFormData(prevState => ({
                    ...prevState,
                    [field]: selectedItem[optionKey] || "",
                    [idField]: selectedItem.id || ""
                }));
    
                // If part_id is changed, update part_name
                if (field === "part_id") {
                    const selectedPart = options.parts.find(part => part.id === selectedItem.id);
                    if (selectedPart) {
                        setFormData(prevState => ({
                            ...prevState,
                            part_name: selectedPart.product_name // Assuming part_name is a field in the parts data
                        }));
                    }
                }

                // If chemical_id is changed, update chemical_name
                if (field === "chemical_id") {
                    const selectedChemical = options.chemicals.find(chemical => chemical.id === selectedItem.id);
                    if (selectedChemical) {
                        setFormData(prevState => ({
                            ...prevState,
                            chemical_name: selectedChemical.product_name // Assuming product_name is a field in the chemicals data
                        }));
                    }
                }

            } else {
                // Update the form data for the selected field
                setFormData(prevState => ({
                    ...prevState,
                    [field]: value
                }));
    
                // If part_id is changed, update part_name
                if (field === "part_id") {
                    const selectedPart = options.parts.find(part => part.id === value);
                    if (selectedPart) {
                        setFormData(prevState => ({
                            ...prevState,
                            part_name: selectedPart.product_name // Assuming part_name is a field in the parts data
                        }));
                    }
                }

                // If chemical_id is changed, update chemical_name
                if (field === "chemical_id") {
                    const selectedChemical = options.chemicals.find(chemical => chemical.id === value);
                    if (selectedChemical) {
                        setFormData(prevState => ({
                            ...prevState,
                            chemical_name: selectedChemical.product_name // Assuming product_name is a field in the chemicals data
                        }));
                    }
                }

            }
        }
    };

    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     onSave(formData);
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        // Combine form data with chemical entries
        const payload = {
            ...formData,
            chemicals: chemicalEntries
                .filter(entry => entry.chemical_id && entry.chemical_quantity) // Filter out entries with empty chemical_id or chemical_quantity
                .map(entry => ({
                    chemical_id: entry.chemical_id,
                    chemical_name: entry.chemical_name,
                    chemical_quantity: entry.chemical_quantity,
                })),
        };
    
        // Call onSave with the combined payload
        onSave(payload);
    };

    const addChemicalEntry = () => {
        setChemicalEntries([...chemicalEntries, { chemical_id: "", chemical_quantity: "" }]);
    };

    const removeChemicalEntry = (index) => {
        const newEntries = chemicalEntries.filter((_, i) => i !== index);
        setChemicalEntries(newEntries);
    };

    const handleChemicalChange = (index, field, value) => {
    const newEntries = [...chemicalEntries];
    newEntries[index][field] = value;
    // Check if the selected value is "other" to show the new chemical form
    if (value === "other") {
        setShowNewChemicalForm(true);
        onExpandDrawer(); // Ensure the drawer expands if needed
    } else {
        // If the chemical_id is changed, update the chemical_name
        if (field === 'chemical_id') {
            const selectedChemical = options.chemicals.find(chemical => chemical.id === value);
            newEntries[index].chemical_name = selectedChemical ? selectedChemical.product_name : ""; // Update chemical_name based on selection
        }
    }
    setChemicalEntries(newEntries);
};

    const handleNewProductSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`, {
                product_name: formData.new_product_name,
                full_name: formData.full_name,
                // unit_cost: formData.unit_cost,
                descrip: formData.descrip
            });
            resetNewProductForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new product:", error);
            setErrorMessage("Failed to add new product. Please try again.");
        }
    };

    const handleNewRawMaterialSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`, {
                product_code: formData.new_RM_product_code,
                product_name: formData.new_RM_product_name,
                quantity: formData.new_RM_quantity,
                daily_consumption: formData.new_RM_daily_consumption,
                backup_time: formData.new_RM_backup_time,
                base_uom: formData.new_RM_base_uom,
                // cost_per_unit: formData.new_RM_cost_per_unit
            });
            resetNewRawMaterialForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new raw material:", error);
            setErrorMessage("Failed to add new raw material. Please try again.");
        }
    };

    const handleNewChemicalSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`, {
                product_code: formData.new_CH_product_code,
                product_name: formData.new_CH_product_name,
                quantity: formData.new_CH_quantity,
                daily_consumption: formData.new_CH_daily_consumption,
                backup_time: formData.new_CH_backup_time,
                base_uom: formData.new_CH_base_uom,
                // cost_per_unit: formData.new_CH_cost_per_unit
            });
            resetNewChemicalForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new chemical:", error);
            setErrorMessage("Failed to add new chemical. Please try again.");
        }
    };

    const resetNewProductForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_product_name: "",
            full_name: "",
            // unit_cost: "",
            descrip: ""
        }));
        setShowNewProductForm(false);
    };

    const resetNewRawMaterialForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_RM_product_code: "",
            new_RM_product_name: "",
            new_RM_quantity: "",
            new_RM_daily_consumption: "",
            new_RM_backup_time: "",
            new_RM_base_uom: "",
            // new_RM_cost_per_unit: ""
        }));
        setShowNewRawMaterialForm(false);
    };

    const resetNewChemicalForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_CH_product_code: "",
            new_CH_product_name: "",
            new_CH_quantity: "",
            new_CH_daily_consumption: "",
            new_CH_backup_time: "",
            new_CH_base_uom: "",
            // new_CH_cost_per_unit: ""
        }));
        setShowNewChemicalForm(false);
    };

    return (
        <>
            {/* Header */}
            <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, paddingBottom: 1, bgcolor: grey[200], p: 2, textAlign: "center" }}>
                <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center", mb: 1, mt: 1 }}>
                    {editData ? "Edit BOM Entry" : "Add BOM Entry"}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
            {/* Main Form Box */}
            <Box sx={{ ml: 2, mt: 2, mr: 2, mb: 0, p: 2, border: "1px solid #ccc", borderRadius: 2, flex: 1 }}>
                
                <Box sx={{ display: "flex", gap: 2, marginTop: 1, marginBottom: 1 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Select Product</InputLabel>
                                    <Select
                                        name="product_id"
                                        value={formData.product_id}
                                        onChange={(e) => handleChange(e, "product_id", "product_id", options.products, "id")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Product
                                        </MenuItem>
                                        {options.products.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.product_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>           

                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Select Raw Material</InputLabel>
                                    <Select
                                        name="part_id"
                                        value={formData.part_id}
                                        onChange={(e) => handleChange(e, "part_id", "part_id", options.parts, "id")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Raw Material
                                        </MenuItem>
                                        {options.parts.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.product_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <TextField 
                                    fullWidth 
                                    margin="dense" 
                                    size="small" 
                                    label="Quantity Required" 
                                    name="quantity_required" 
                                    type="number" 
                                    value={formData.quantity_required} 
                                    onChange={(e) => handleChange(e, "quantity_required")} // Use handleChange
                                />
                            </Grid> 

                    {chemicalEntries.map((entry, index) => (
                       <Grid container spacing={3} key={index} alignItems="center">
                           <Grid item xs={6} style={{ marginTop: '14px' }}>
                               <FormControl fullWidth margin="dense" size="small" style={{ marginLeft: '16px' }}>
                                   <InputLabel>Select Chemical</InputLabel>
                                   <Select name={`chemical_id_${index}`} value={entry.chemical_id} onChange={(e) => handleChemicalChange(index, 'chemical_id', e.target.value)}>
                                    <MenuItem value="">Select an option</MenuItem>
                                    <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Chemical
                                        </MenuItem>
                                       {options.chemicals.map(item => <MenuItem key={item.id} value={item.id}>{item.product_name}</MenuItem>)}
                                   </Select>
                               </FormControl>
                           </Grid>
                           <Grid item xs={4.5} style={{ marginLeft: '8px', marginTop: '14px' }}>
                               <TextField fullWidth margin="dense" size="small" label="Chemical Quantity" name={`chemical_quantity_${index}`} type="number" value={entry.chemical_quantity} onChange={(e) => handleChemicalChange(index, 'chemical_quantity', e.target.value)} />
                           </Grid>
                           <Grid item xs={1}  style={{ marginTop: '14px' }}>
                               <IconButton onClick={() => removeChemicalEntry(index)}>
                               <RemoveCircleIcon color="error" />
                               </IconButton>
                           </Grid>
                       </Grid>
                   ))}
                   <Grid item xs={6}>
                       <Button color="secondary" onClick={addChemicalEntry}>Add More Chemicals</Button>
                   </Grid>
                </Grid>

                        {/* Buttons */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={onClose}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Box>

            {/* New Product Form */}
            {showNewProductForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Product Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewProductSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Enter Product"
                                    name="new_product_name"
                                    value={formData.new_product_name}
                                    onChange={(e) => setFormData({ ...formData, new_product_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Full Name"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Unit Cost"
                                    name="unit_cost"
                                    value={formData.unit_cost}
                                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                                />
                            </Grid> */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Description"
                                    name="descrip"
                                    value={formData.descrip}
                                    onChange={(e) => setFormData({ ...formData, descrip: e.target.value })}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new product form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewProductForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Raw Material Form */}
            {showNewRawMaterialForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Raw Material Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewRawMaterialSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Product Code"
                                    name="new_RM_product_code"
                                    value={formData.new_RM_product_code}
                                    onChange={(e) => setFormData({ ...formData, new_RM_product_code: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Product Name"
                                    name="new_RM_product_name"
                                    value={formData.new_RM_product_name}
                                    onChange={(e) => setFormData({ ...formData, new_RM_product_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Quantity"
                                    name="new_RM_quantity"
                                    value={formData.new_RM_quantity}
                                    onChange={(e) => setFormData({ ...formData, new_RM_quantity: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Daily Consumption"
                                    name="new_RM_daily_consumption"
                                    value={formData.new_RM_daily_consumption}
                                    onChange={(e) => setFormData({ ...formData, new_RM_daily_consumption: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Backup Time"
                                    name="new_RM_backup_time"
                                    value={formData.new_RM_backup_time}
                                    onChange={(e) => setFormData({ ...formData, new_RM_backup_time: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Base UOM"
                                    name="new_RM_base_uom"
                                    value={formData.new_RM_base_uom}
                                    onChange={(e) => setFormData({ ...formData, new_RM_base_uom: e.target.value })}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Cost per unit"
                                    name="new_RM_cost_per_unit"
                                    value={formData.new_RM_cost_per_unit}
                                    onChange={(e) => setFormData({ ...formData, new_RM_cost_per_unit: e.target.value })}
                                />
                            </Grid> */}
                        </Grid>

                        {/* Buttons for the new raw material form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewRawMaterialForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Chemical Form */}
            {showNewChemicalForm && (
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                        <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                                New Chemical Details
                            </Typography>
                        </Box>
                        <form onSubmit={handleNewChemicalSubmit}>
                            <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Chemical Code"
                                    name="new_CH_product_code"
                                    value={formData.new_CH_product_code}
                                    onChange={(e) => setFormData({ ...formData, new_CH_product_code: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Chemical Name"
                                    name="new_CH_product_name"
                                    value={formData.new_CH_product_name}
                                    onChange={(e) => setFormData({ ...formData, new_CH_product_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Quantity"
                                    name="new_CH_quantity"
                                    value={formData.new_CH_quantity}
                                    onChange={(e) => setFormData({ ...formData, new_CH_quantity: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Daily Consumption"
                                    name="new_CH_daily_consumption"
                                    value={formData.new_CH_daily_consumption}
                                    onChange={(e) => setFormData({ ...formData, new_CH_daily_consumption: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Backup Time"
                                    name="new_CH_backup_time"
                                    value={formData.new_CH_backup_time}
                                    onChange={(e) => setFormData({ ...formData, new_CH_backup_time: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Base UOM"
                                    name="new_CH_base_uom"
                                    value={formData.new_CH_base_uom}
                                    onChange={(e) => setFormData({ ...formData, new_CH_base_uom: e.target.value })}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Cost per unit"
                                    name="new_CH_cost_per_unit"
                                    value={formData.new_CH_cost_per_unit}
                                    onChange={(e) => setFormData({ ...formData, new_CH_cost_per_unit: e.target.value })}
                                />
                            </Grid> */}
                            </Grid>

                            {/* Buttons for the new chemical form */}
                            <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                                <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                    Save
                                </Button>
                                <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewChemicalForm(false)}>
                                    Cancel
                                </Button>
                            </Box>
                        </form>
                    </Box>
                )}

            </Box>
        </>
    );
};

export default AddEditBOM;